"use client";
import { collection } from "firebase/firestore";
import { db } from "../lib/firebase";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AiOutlineShoppingCart } from "react-icons/ai";
// import products from "../data/products";
import { motion, AnimatePresence } from "framer-motion";
import { onSnapshot } from "firebase/firestore";

// 🔥 WhatsApp message
const getWhatsAppMessage = (product) => {
  if (!product) return "Hi! I have a query.";

  return `Hello, I'm interested in this solar product:

🔹 Product: ${product.title}
🔹 Brand: ${product.brand}
🔹 Category: ${product.category}
🔹 Price: ₦${product.price.toLocaleString()}

I'd like guidance on:
• Whether this is suitable for my needs
• What capacity I actually require
• Installation process and pricing

Looking forward to your recommendation.`;
};

// 🔥 Group by brand
const groupByBrand = (items) => {
  return items.reduce((acc, product) => {
    if (!acc[product.brand]) acc[product.brand] = [];
    acc[product.brand].push(product);
    return acc;
  }, {});
};

// 🔥 Brand styling
const brandStyles = {
  Deye: { text: "text-green-600", bg: "bg-green-600" },
  "LV Topsun": { text: "text-orange-500", bg: "bg-orange-500" },
  Sako: { text: "text-green-700", bg: "bg-green-700" },
  Haisic: { text: "text-blue-600", bg: "bg-blue-600" },
  Deriy: { text: "text-orange-600", bg: "bg-orange-600" },
};

// 🔥 Keyword mapping
const keywordMap = {
  inverter: ["inverter", "inverters"],
  battery: ["battery", "batteries"],
  panel: ["panel", "panels", "solar panel"],
};

const normalize = (text) => text.toLowerCase().replace(/s$/, "");

const ShopSection = () => {
  const [selectedBrand, setSelectedBrand] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [setRecentlyViewed] = useState([]);
  const [touchStartY, setTouchStartY] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // 🔥 Brands
  const allBrands = useMemo(() => {
    return ["All", ...new Set(products.map((p) => p.brand))].sort();
  }, [products]);

  // 🔥 Categories
  const filteredCategories = [
    "All",
    ...new Set(
      products
        .filter((p) =>
          selectedBrand === "All" ? true : p.brand === selectedBrand,
        )
        .map((p) => p.category),
    ),
  ];

  const SkeletonCard = () => {
    return (
      <div className="bg-white rounded-3xl overflow-hidden shadow-md animate-pulse">
        {/* Image skeleton */}
        <div className="w-full h-64 bg-gray-300" />

        <div className="p-5 space-y-3">
          {/* Title */}
          <div className="h-4 bg-gray-300 rounded w-3/4" />

          {/* Price */}
          <div className="h-4 bg-gray-300 rounded w-1/2" />

          {/* Button */}
          <div className="h-10 bg-gray-300 rounded-xl w-full mt-4" />
        </div>
      </div>
    );
  };

  // 🔥 Suggestions (Amazon style)
  const suggestions = useMemo(() => {
    if (!search) return [];

    const lower = search.toLowerCase();

    const categorySuggestions = Object.values(keywordMap)
      .flat()
      .filter((word) => word.includes(lower));

    const productSuggestions = products
      .map((p) => p.title)
      .filter((title) => title.toLowerCase().includes(lower));

    return [...new Set([...categorySuggestions, ...productSuggestions])].slice(
      0,
      6,
    );
  }, [search]);

  // 🔥 Filtering logic
  const filteredProducts = useMemo(() => {
    const searchText = normalize(search);

    return products.filter((product) => {
      if (!product.active) return false; // 🚀 HIDE archived products
      if (!product) return false;

      const searchableText = `
      ${product.title || ""}
      ${product.brand || ""}
      ${product.category || ""}
      ${product.description || ""}
    `.toLowerCase();

      const matchesSearch = searchableText.includes(searchText);

      const matchesKeyword = Object.entries(keywordMap).some(
        ([key, variations]) =>
          variations.some((word) => search.toLowerCase().includes(word)) &&
          (product.category || "").toLowerCase().includes(key),
      );

      if (search && !matchesSearch && !matchesKeyword) return false;

      if (selectedBrand !== "All" && product.brand !== selectedBrand)
        return false;

      if (selectedCategory !== "All" && product.category !== selectedCategory)
        return false;

      return true;
    });
  }, [products, search, selectedBrand, selectedCategory]);

  const groupedProducts = useMemo(
    () => groupByBrand(filteredProducts),
    [filteredProducts],
  );

  const recommended = useMemo(() => {
    if (!selectedProduct) return [];
    return products
      .filter(
        (p) =>
          p.category === selectedProduct.category &&
          p.title !== selectedProduct.title,
      )
      .slice(0, 4);
  }, [selectedProduct]);

  const openProduct = (product) => {
    setSelectedProduct(product);

    setRecentlyViewed((prev) => {
      const exists = prev.find((p) => p.title === product.title);
      if (exists) return prev;
      return [product, ...prev].slice(0, 5);
    });
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "products"),
      (snapshot) => {
        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((p) => p.active !== false);

        console.log("🔥 Real-time products:", data);

        setProducts(data);
        setLoading(false); // ✅ FIX HERE
      },
      (error) => {
        console.error("❌ Firestore error:", error);
        setLoading(false); // ✅ ALSO HANDLE ERROR
      },
    );

    return () => unsubscribe();
  }, []);

  // ESC close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedProduct(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // swipe
  const handleTouchStart = (e) => setTouchStartY(e.touches[0].clientY);

  const handleTouchMove = (e) => {
    if (!touchStartY) return;
    if (e.touches[0].clientY - touchStartY > 120) setSelectedProduct(null);
  };

  if (loading) {
    return (
      <>
        <Navbar />

        <div className="px-6 py-12 bg-gradient-to-b from-gray-50 to-white">
          <h2 className="text-3xl font-semibold text-center mb-8">Shop</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>

        <Footer />
      </>
    );
  }

  console.log("Products in state:", products);

  return (
    <>
      <Helmet>
        <title>
          Solar Inverters, Batteries & Solar Panels in Lagos, Nigeria | Solaris
          Surge
        </title>
        <meta
          name="description"
          content="Shop premium solar inverters, lithium batteries, solar panels and complete solar power systems in Lagos, Nigeria. Trusted solar solutions for homes and businesses."
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Store",
            name: "Solaris Surge Energy Solutions",
            url: "https://www.solarissurge.net/shop",
            telephone: "+2348165213568",
            address: {
              "@type": "PostalAddress",
              streetAddress:
                "B640 Electronics Section, Alaba International Market",
              addressLocality: "Ojo",
              addressRegion: "Lagos",
              addressCountry: "NG",
            },
          })}
        </script>
      </Helmet>

      <Navbar />

      <div className="px-6 py-12 bg-gradient-to-b from-gray-50 to-white">
        <h1 className="text-3xl font-semibold text-center mb-2">
          Solar Inverters, Batteries & Solar Panels in Lagos
        </h1>

        <p className="text-gray-600 text-center mb-8">
          Premium solar solutions for complete energy independence.
        </p>

        <section className="max-w-4xl mx-auto mb-10 text-center">
          <p className="text-gray-700 leading-relaxed">
            Solaris Surge Energy Solutions supplies high-quality solar
            inverters, lithium batteries, solar panels, charge controllers, and
            complete solar power systems for homes and businesses across Lagos,
            Nigeria.
          </p>

          <p className="text-gray-700 leading-relaxed mt-4">
            Whether you need a backup power solution for your home or a
            large-scale energy system for your business, we provide reliable
            solar products from trusted brands at competitive prices.
          </p>
        </section>

        {/* 🔍 SEARCH */}
        <div className="max-w-md mx-auto mb-6 relative">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="Search products..."
            className="w-full px-4 py-3 rounded-xl border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />

          {/* 🔥 Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute w-full bg-white shadow-lg rounded-xl mt-2 z-50 border">
              {suggestions.map((item, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setSearch(item);
                    setShowSuggestions(false);
                  }}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 🔥 BRAND PILLS */}
        <div className="flex flex-wrap gap-3 justify-center mb-6">
          {allBrands.map((brand) => (
            <button
              key={brand}
              onClick={() => {
                setSelectedBrand(brand);
                setSelectedCategory("All");
              }}
              className={`px-4 py-2 rounded-xl text-sm ${
                selectedBrand === brand
                  ? "bg-green-600 text-white"
                  : "bg-gray-200"
              }`}
            >
              {brand}
            </button>
          ))}
        </div>

        {/* 🔥 CATEGORY PILLS */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {filteredCategories.map((cat, index) => (
            <button key={`${cat}-${index}`}>{cat}</button>
          ))}
        </div>

        {/* 🔥 PRODUCTS */}
        <div className="max-w-7xl mx-auto">
          {Object.keys(groupedProducts).length === 0 && (
            <p className="text-center text-gray-500">No products found.</p>
          )}

          {Object.keys(groupedProducts).map((brand) => (
            <div key={brand} className="mb-12">
              {/* BRAND HEADER */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-2 h-6 rounded-full ${
                    brandStyles[brand]?.bg || "bg-gray-400"
                  }`}
                />
                <h3
                  className={`text-xl font-bold ${
                    brandStyles[brand]?.text || "text-gray-800"
                  }`}
                >
                  {brand}
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {groupedProducts[brand].map((product) => (
                  <motion.div
                    key={product.id || product.title}
                    onClick={() => openProduct(product)}
                    className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl cursor-pointer"
                  >
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-64 object-cover"
                    />

                    <div className="p-5">
                      <h3 className="text-sm font-semibold truncate">
                        {product.title}
                      </h3>

                      <p className="text-green-700 font-bold">
                        ₦{product.price.toLocaleString()}
                      </p>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const message = getWhatsAppMessage(product);
                          window.open(
                            `https://wa.me/+2348165213568?text=${encodeURIComponent(
                              message,
                            )}`,
                            "_blank",
                          );
                        }}
                        className="mt-4 w-full bg-black text-white py-2 rounded-xl flex items-center justify-center gap-2"
                      >
                        <AiOutlineShoppingCart />
                        Talk to Expert
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ MODAL (UNCHANGED) */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10"
            onClick={() => setSelectedProduct(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-green/50 backdrop-blur-md" />

            <motion.div
              className="relative bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col z-10"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              initial={{ scale: 0.95, y: 40, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/80 shadow-md text-xl"
              >
                ×
              </button>

              <div className="overflow-y-auto flex-1">
                <div className="grid md:grid-cols-2">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full md:h-[500px] object-cover"
                  />

                  <div className="p-8 space-y-4">
                    <h2 className="text-2xl font-semibold">
                      {selectedProduct.title}
                    </h2>

                    <p className="text-gray-500 text-sm">
                      {selectedProduct.brand} • {selectedProduct.category}
                    </p>

                    <p className="text-2xl font-bold text-green-700">
                      ₦{selectedProduct.price.toLocaleString()}
                    </p>

                    <div className="text-sm text-gray-600 space-y-2">
                      {Array.isArray(selectedProduct.description)
                        ? selectedProduct.description.map((item, i) => (
                            <p key={i}>✔ {item}</p>
                          ))
                        : selectedProduct.description}
                    </div>
                  </div>
                </div>

                {recommended.length > 0 && (
                  <div className="px-8 py-6 border-t">
                    <h3 className="text-sm font-semibold mb-4">
                      Recommended for you
                    </h3>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {recommended.map((item) => (
                        <div
                          key={item.title}
                          onClick={() => openProduct(item)}
                          className="cursor-pointer"
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            className="h-24 w-full object-cover rounded-xl"
                          />
                          <p className="text-xs mt-1 truncate">{item.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t p-4">
                <button className="w-full bg-black text-white py-3 rounded-xl">
                  Talk to Expert on WhatsApp
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <section className="bg-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">
            Choosing the Right Solar Products
          </h2>

          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Selecting the right solar inverter, battery, and solar panel setup
              depends on your energy consumption, budget, and power
              requirements.
            </p>

            <p>
              Our team helps customers throughout Lagos choose reliable solar
              products that deliver long-term performance and energy savings.
            </p>

            <p>
              We supply premium lithium batteries, hybrid inverters, MPPT charge
              controllers, and high-efficiency solar panels suitable for homes,
              offices, schools, churches, and commercial facilities.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ShopSection;
